/**
 * Water-quantity roles: what a stated water figure IS, bound to one number.
 *
 * A gal/day (or MGD, AFY, gal/MWh) figure in a source quote or a report
 * sentence can be any of several different quantities that share a unit and
 * sometimes a value: a whole-site service allocation, an average or peak
 * whole-site/project demand, the allocation-minus-peak margin, a cooling or
 * process consumption, a consumptive-use limit (entitlement, right, permitted
 * consumptive capacity), a withdrawal/makeup flow, a firm utility delivery, or
 * a utility-system operating figure. River Ridge showed what happens when they
 * are conflated: a 75,000 gal/day site allocation became "consumptive-use
 * capacity", a 55,000 gal/day whole-site peak became cooling consumption AND
 * cooling withdrawal, and the 20,000 gal/day allocation margin became a
 * passing "consumption headroom".
 *
 * This module is the single shared vocabulary for those roles. It is pure and
 * IO-free: no services, no store, no model calls, no mutation of its inputs.
 *
 * ---------------------------------------------------------------------------
 * STABLE EXPORTED API (consumed by units W2-W8; keep names and types stable)
 * ---------------------------------------------------------------------------
 *
 * Roles
 *   WATER_QUANTITY_ROLES: readonly WaterQuantityRole[]
 *   type WaterQuantityRole =
 *     | 'site-allocation'          service allocation/allotment/reservation to the site
 *     | 'facility-demand-average'  average whole-site/project demand (not cooling)
 *     | 'facility-demand-peak'     peak/maximum/design-day whole-site/project demand
 *     | 'cooling-consumption'      consumptive use / evaporation of the process
 *     | 'consumptive-use-limit'    entitlement/right/permitted/available consumptive use
 *     | 'withdrawal-makeup'        withdrawal, makeup or intake flow
 *     | 'firm-utility-delivery'    firm/contracted/committed delivery to the site
 *     | 'allocation-margin'        margin/headroom (allocation minus demand)
 *     | 'utility-system-operating' utility/plant production, processing, treatment
 *   WHOLE_SITE_WATER_QUANTITY_ROLES: ReadonlySet<WaterQuantityRole>
 *     The four roles that describe the whole site (allocation, average and peak
 *     demand, allocation margin). None is a cooling-process quantity, a
 *     consumptive-use limit or a firm delivery.
 *
 * Text classifier (no unit conversion, no ledger access)
 *   classifyWaterQuantityRole(text: string, tokenStart: number, tokenEnd: number)
 *     : WaterQuantityRole | null
 *     Classifies the number printed at text.slice(tokenStart, tokenEnd).
 *     `null` means UNKNOWN and is the fail-safe answer; callers must never
 *     substitute another role for it.
 *   waterNumericTokens(text: string): WaterNumericToken[]
 *     Unsigned numeric tokens ({ start, end, raw, value }) in reading order, so
 *     a caller can locate the token for a stored value (e.g. a fact's raw
 *     value) and classify it. Values are the printed magnitude; no units are
 *     applied ("1.5 million gallons" gives value 1.5).
 *
 * SED whole-site ledger matcher
 *   GALLONS_PER_MGD = 1_000_000   (same constant as screeningEvidencePrefill
 *     GALLONS_PER_MGD and generationScreeningExtraction MGD_TO_GALLONS_PER_DAY;
 *     a test pins all three equal)
 *   DEFAULT_WHOLE_SITE_WATER_TOLERANCE_GAL_PER_DAY = 0.5
 *   WHOLE_SITE_WATER_LEDGER_KEYS: readonly WholeSiteWaterLedgerKey[]
 *     ['siteWaterAllocationMgd', 'projectAverageWaterDemandMgd',
 *      'projectPeakWaterDemandMgd', 'projectWaterMarginMgd']
 *   WHOLE_SITE_WATER_LEDGER_ROLES: Readonly<Record<WholeSiteWaterLedgerKey, WaterQuantityRole>>
 *   interface WholeSiteWaterLedgerEntry { key, role, valueMgd, galPerDay, assumed, fictional }
 *   wholeSiteWaterLedger(sed): WholeSiteWaterLedgerEntry[]
 *     The current, accepted whole-site rows: present in siteResourceViewFromSed
 *     (hash-bound, manifest-bound, non-conflicting), backed by an exact
 *     siteResourceSourceRowsFromSed witness, and with no human field review
 *     other than 'accepted' (a 'rejected' or 'corrected' review removes it).
 *   matchWholeSiteWaterRoles(ledger, galPerDay, tolGalPerDay = 0.5): WholeSiteWaterLedgerKey[]
 *   wholeSiteWaterRolesAt(sed, galPerDay, tolGalPerDay = 0.5): WholeSiteWaterLedgerKey[]
 *     Which whole-site ledger keys have a value equal to the daily rate, in
 *     WHOLE_SITE_WATER_LEDGER_KEYS order. [] for a missing/invalid ledger, a
 *     non-finite or negative rate, or no match.
 *
 * ---------------------------------------------------------------------------
 * CLASSIFIER RULES
 * ---------------------------------------------------------------------------
 *
 * Binding. Only the label phrase that owns THIS token is read:
 *  - The clause starts after the last newline, ';', ' | ', bullet, sentence
 *    end (abbreviation dots such as "U.S." excluded) or contrastive connector
 *    (but / however / whereas / although), at most 300 characters back.
 *  - Inside the clause, a role attached to an earlier water quantity+unit
 *    cannot own this token (the carry rule of waterUseScreeningGuard
 *    sourceWaterRole). The label phrase starts after that quantity, and after
 *    any role noun that immediately followed it and was that quantity's role.
 *  - Equivalence carry: "0.075 MGD, equivalent to 75,000 gallons/day",
 *    "75,000 gpd (0.075 MGD)" and "X = Y" give the second number the first
 *    number's role.
 *  - Statistic carry: "production of 1.5 MGD with a max of 4.6 MGD" or
 *    "average demand of 25,000 gpd and a peak of 55,000 gpd" keeps the subject
 *    and takes the new statistic. Allocation, limit, margin and firm-delivery
 *    roles have no average/peak, so they carry to null.
 *  - When the label phrase has no role, an immediately following role noun is
 *    read ("75,000 gpd allocation", "55,000 gpd of consumptive use"): only the
 *    unit, one parenthetical and "of" may intervene, and a disclaimed noun
 *    ("55,000 gpd consumption unknown") is not read. A purpose phrase ("for
 *    potable and process consumption") never counts, before or after.
 *  - A clause with "respectively" is ambiguous and gives null.
 *
 * Negation. A cue is ignored when preceded in its phrase by not/no/never/
 * without/neither/nor (+ up to three articles), a "non-" prefix, "does not
 * establish/represent/constitute/mean/imply ...", "cannot/must not/should not
 * be treated/described/classified/labelled/used as ...", or "rather than /
 * instead of / excluding / separate from / other than ..."; or when its own
 * tail says unknown / unspecified / unconfirmed / unquantified / not (yet)
 * known / established / determined / specified / quantified / confirmed /
 * modeled. These are the waterUseScreeningGuard explicitWaterFlow idioms.
 *
 * Precedence (first match wins):
 *   withdrawal-makeup > allocation-margin > facility-demand-* >
 *   consumptive-use-limit > cooling-consumption > site-allocation >
 *   firm-utility-delivery > utility-system-operating > null.
 * Refinements:
 *  - "consumptive(-use) allocation / allotment / entitlement / right /
 *    allowance / limit" in one noun phrase is consumptive-use-limit.
 *  - Consumption with a limit cue (entitlement, right, limit, allowance,
 *    permitted, authorized, capacity, available) in the same phrase is a
 *    consumptive-use-limit; "at full/design/rated capacity" is an operating
 *    condition, not a limit.
 *  - Whole-site scope (whole-site, site-/facility-/campus-/project-wide, total
 *    site/project/facility/campus, all uses, potable/process) never yields
 *    cooling-consumption. With an average/peak statistic and a use, demand or
 *    consumption noun it is a facility demand; otherwise it falls through
 *    (an allocation stays an allocation; bare whole-site use is null).
 *  - Demand words, weak margin words (remaining, spare, surplus) and weak
 *    ceiling cues ("up to", "reserve capacity") describe a utility system when
 *    the phrase names only a utility/plant/system actor or operation and no
 *    subject site; they then give utility-system-operating.
 *
 * The number need not be followed by a unit ("consumptive intensity in US
 * gallons per MWh of facility electricity is 24"); callers bind units
 * themselves. Invalid offsets give null.
 */
import type { SiteResourceKey } from '../../types/siteResourceEvidence';
import {
  siteResourceSourceRowsFromSed,
  siteResourceViewFromSed,
  type SiteResourceSedInput,
} from '../sed/siteResourceDocumentProjection';

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

export const WATER_QUANTITY_ROLES = [
  'site-allocation',
  'facility-demand-average',
  'facility-demand-peak',
  'cooling-consumption',
  'consumptive-use-limit',
  'withdrawal-makeup',
  'firm-utility-delivery',
  'allocation-margin',
  'utility-system-operating',
] as const;
export type WaterQuantityRole = typeof WATER_QUANTITY_ROLES[number];

/** Whole-site roles. None of them is a cooling-process quantity. */
export const WHOLE_SITE_WATER_QUANTITY_ROLES: ReadonlySet<WaterQuantityRole> = new Set<WaterQuantityRole>([
  'site-allocation',
  'facility-demand-average',
  'facility-demand-peak',
  'allocation-margin',
]);

// ---------------------------------------------------------------------------
// Numeric tokens and water units
// ---------------------------------------------------------------------------

/** An unsigned printed number. `value` is the printed magnitude only. */
export interface WaterNumericToken {
  readonly start: number;
  readonly end: number;
  readonly raw: string;
  readonly value: number;
}

/** Same grouping/decimal/exponent grammar as the extraction validator's
 * numericSourceTokens, without a sign, and without a trailing sentence dot. */
const NUMBER_SOURCE = String.raw`(?<![\w.,])(?:(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?|\.\d+)(?:e[+-]?\d+)?(?![\d_]|[.,]\d)`;

const PER = String.raw`\s*(?:\/|per)\s*`;
const RATE_PERIOD = String.raw`(?:calendar\s+)?(?:day|d|hour|hr|h|minute|min|year|yr|annum)`;
const ENERGY_BASIS = String.raw`(?:MWh|kWh)(?:[- ](?:IT|gross|net|facility))?`;
/** Water volume or rate units only. Power/energy (MW, MWh) is not a water quantity. */
const WATER_UNIT_SOURCE = [
  String.raw`(?:(?:million|thousand|billion)\s+)?(?:(?:US|U\.S\.|imperial)\s+)?(?:gallons?|gals?)\b(?:${PER}(?:${RATE_PERIOD}|${ENERGY_BASIS})\b)?`,
  String.raw`(?:MGD|MG\/d|gpd|gpm|gph|AFY|AFA)\b`,
  String.raw`acre[- ]?(?:feet|foot|ft)\b(?:${PER}(?:year|yr|annum)\b)?`,
  String.raw`(?:mega)?(?:lit(?:er|re)s?|L)\b(?:${PER}(?:day|d|second|sec|s|${ENERGY_BASIS})\b)?`,
  String.raw`(?:ML\/d|MLD)\b`,
  String.raw`(?:m3|m³|cubic\s+met(?:er|re)s?)(?:${PER}(?:day|d|hour|hr|h|year|yr)\b)?`,
].join('|');

const NUMBER_PATTERN = new RegExp(NUMBER_SOURCE, 'gi');
const WATER_QUANTITY_PATTERN = new RegExp(`(${NUMBER_SOURCE})\\s*-?\\s*(?:${WATER_UNIT_SOURCE})`, 'gi');
const LEADING_WATER_UNIT = new RegExp(`^\\s*-?\\s*(?:${WATER_UNIT_SOURCE})`, 'i');

export function waterNumericTokens(text: string): WaterNumericToken[] {
  return [...text.matchAll(NUMBER_PATTERN)].flatMap(match => {
    const raw = match[0];
    const value = Number(raw.replaceAll(',', ''));
    return Number.isFinite(value) ? [{ start: match.index!, end: match.index! + raw.length, raw, value }] : [];
  });
}

interface WaterQuantitySpan {
  /** Start of the number. */
  start: number;
  /** End of the number (start of the unit). */
  numberEnd: number;
  /** End of the unit. */
  end: number;
}

/** The last complete water quantity+unit inside [from, to). */
function lastWaterQuantity(text: string, from: number, to: number): WaterQuantitySpan | undefined {
  const window = text.slice(from, to);
  let last: WaterQuantitySpan | undefined;
  for (const match of window.matchAll(WATER_QUANTITY_PATTERN)) {
    const start = from + match.index!;
    last = { start, numberEnd: start + match[1]!.length, end: start + match[0].length };
  }
  return last;
}

// ---------------------------------------------------------------------------
// Clauses
// ---------------------------------------------------------------------------

const LABEL_WINDOW_CHARS = 300;
const MAX_CARRY_DEPTH = 8;
const CLAUSE_BREAK = /\n|;|•|\s\|\s|[.!?](?=\s|$)|\b(?:but|however|whereas|although)\b/gi;
const ABBREVIATION = /^(?:[A-Za-z]|U\.S|e\.g|i\.e|approx|est|incl|vs|no|fig|ref|inc|co|corp|ltd|st|ca|cf|al|dept|div)$/i;

function isAbbreviationDot(window: string, index: number): boolean {
  if (window[index] !== '.') return false;
  const word = /([A-Za-z][A-Za-z.]*)$/.exec(window.slice(0, index))?.[1];
  return !!word && ABBREVIATION.test(word);
}

function clauseStartBefore(text: string, position: number): number {
  const floor = Math.max(0, position - LABEL_WINDOW_CHARS);
  const window = text.slice(floor, position);
  let start = 0;
  for (const match of window.matchAll(CLAUSE_BREAK)) {
    if (isAbbreviationDot(window, match.index!)) continue;
    start = match.index! + match[0].length;
  }
  return floor + start;
}

function clauseEndAfter(text: string, position: number): number {
  const window = text.slice(position, position + LABEL_WINDOW_CHARS);
  for (const match of window.matchAll(CLAUSE_BREAK)) {
    if (isAbbreviationDot(window, match.index!)) continue;
    return position + match.index!;
  }
  return position + window.length;
}

// ---------------------------------------------------------------------------
// Cue vocabulary
// ---------------------------------------------------------------------------

const WITHDRAWAL_CUE = /\b(?:withdraw(?:al|als|n|s|ing)?|make[- ]?up|intake|abstraction)\b/gi;
const MARGIN_STRONG_CUE = /\b(?:margin|headroom|minus|net\s+of|less(?!\s+than))\b/gi;
const MARGIN_WEAK_CUE = /\b(?:remaining|spare|surplus|leftover)\b/gi;
const DEMAND_CUE = /\b(average|avg|mean|peak|maximum|max|design[- ]day)\s+(?:(?:total|whole[- ]site|site[- ]wide|facility[- ]wide|campus[- ]wide|project[- ]wide|project|site|facility|campus|water|daily|day|potable|process)\s+){0,3}(?:demands?|use|usage|requirements?)\b/gi;
const STATISTIC_CUE = /\b(average|avg|mean|peak|maximum|max|design[- ]day)\b/gi;
const USE_NOUN_CUE = /\b(?:use|usage|demands?|requirements?|needs?)\b/gi;
const WHOLE_SITE_SCOPE_CUE = /\b(?:whole[- ]site|site[- ]wide|facility[- ]wide|campus[- ]wide|project[- ]wide|total\s+(?:site|project|facility|campus)|all\s+(?:site\s+)?uses|potable(?:\s*\/\s*process)?)\b/gi;
const CONSUMPTION_CUE = /\b(?:consumptive(?:[- ]use)?|consumption|consumed|consum(?:e|es|ing)|evaporat\w*|drift\s+loss(?:es)?)\b/gi;
const CONSUMPTIVE_LIMIT_NOUN_PHRASE = /\bconsumptive(?:[- ]use)?\s+(?:water\s+)?(?:allocations?|allotments?|entitlements?|rights?|allowances?|limits?)\b/gi;
const LIMIT_CUE = /\b(?:entitle(?:d|ments?)|rights?|limits?|limitation|allowance|allowable|permitted|authori[sz](?:ed|ation)|capacity|available|availability)\b/gi;
const OPERATING_CONDITION_BEFORE = /\bat\s+(?:(?:full|design|rated|nameplate|maximum|max|peak)\s+)?$/i;
const ALLOCATION_STRONG_CUE = /\b(?:allocat(?:e|ed|es|ing|ions?)|allot(?:s|ted|ments?)?|reserved|reservations?|set\s+aside|available\s+to\s+(?:the\s+)?(?:site|project|campus|facility|development)|service\s+availability)\b/gi;
const ALLOCATION_WEAK_CUE = /\b(?:up\s+to|reserve(?=\s+capacity))\b/gi;
const FIRM_CUE = /\b(?:firm|contracted|guaranteed|committed|will[- ]serve)\b/gi;
const DELIVERY_CUE = /\b(?:deliver(?:y|ies|ed|able|s)?|supply\s+agreement|delivery\s+point|to\s+(?:the\s+)?site)\b/gi;
const SYSTEM_ACTOR_CUE = /\b(?:utility|utilities|waterworks|(?:water|wastewater)\s+(?:utility|system|department|division|district|authority|provider|supplier|treatment\s+(?:plant|facility)|plant)|sewer\s+system|treatment\s+(?:plant|facility)|wwtp|wtp|municipal\s+(?:system|utility|plant|provider|supplier)|(?:city|county|parish|regional|public)\s+(?:water\s+)?(?:system|utility|plant|water\s+department)|distribution\s+system|system[- ]?wide)\b/gi;
const SYSTEM_OPERATION_CUE = /\b(?:(?:water|wastewater|sewer)\s+(?:production|processing|treatment|pumpage)|production|processing|permitted\s+to\s+(?:produce|process|treat)|(?:plant|treatment|production|system|rated|nameplate|installed)\s+(?:water\s+|wastewater\s+)?capacity)\b/gi;
const SUBJECT_SITE_CUE = /\b(?:site|project|facility|campus|data[- ]cent(?:er|re)s?|development|tenant|customer)\b/i;

const NEGATED_BEFORE: readonly RegExp[] = [
  /\b(?:not|no|never|without|neither|nor)\s+(?:(?:a|an|the|any|yet|currently|as)\s+){0,3}$/i,
  /\bnon[- ]?$/i,
  /\b(?:does|do|did|is|are|was|were|will|would)\s+not\s+(?:establish|represent|constitute|mean|imply|include|reflect|indicate|count\s+as)\s+(?:(?:a|an|the|any)\s+)?(?:[\w/-]+\s+){0,2}$/i,
  /\b(?:cannot|can\s+not|must\s+not|should\s+not|may\s+not|is\s+not|are\s+not)\s+(?:be\s+)?(?:treated|described|classified|labell?ed|used|counted|read|reported|presented)\s+as\s+(?:(?:a|an|the|any)\s+)?(?:[\w/-]+\s+){0,2}$/i,
  /\b(?:rather\s+than|instead\s+of|excluding|exclusive\s+of|separate\s+from|other\s+than|apart\s+from|distinct\s+from)\s+(?:(?:a|an|the|any)\s+)?(?:[\w/-]+\s+){0,3}$/i,
];
const NEGATED_TAIL = /\b(?:unknown|unspecified|unconfirmed|unquantified|undetermined|not\s+(?:yet\s+)?(?:known|established|determined|specified|quantified|confirmed|modell?ed))\b/i;
/** "for potable and process consumption" states a purpose, not a quantity's role. */
const PURPOSE_BEFORE = /\bfor\s+(?:[\w/-]+\s+){0,4}$/i;

/** Words allowed in a role noun that immediately follows the number. */
const FOLLOWING_ROLE_WORD = /^(?:source-stated|stated|daily|day|planning|average|avg|mean|peak|maximum|max|design-day|total|project|site|whole-site|site-wide|facility-wide|campus-wide|project-wide|campus|facility|data-center|data-centre|municipal|utility|potable|potable\/process|process|cooling|cooling-water|cooling-tower|tower|evaporative|consumptive|consumptive-use|non-consumptive|use|usage|demand|requirement|requirements|allocation|allotment|entitlement|right|limit|allowance|capacity|margin|headroom|withdrawal|makeup|make-up|intake|consumption|evaporation|drift|loss|delivery|supply|service|firm|contracted|guaranteed|committed|production|processing|treatment|permitted|net|remaining|spare|surplus|water|wastewater)$/i;
const MAX_FOLLOWING_ROLE_WORDS = 6;

function cueIsNegated(phrase: string, index: number, length: number, purposeSensitive: boolean): boolean {
  const before = phrase.slice(0, index);
  if (NEGATED_BEFORE.some(pattern => pattern.test(before))) return true;
  if (purposeSensitive && PURPOSE_BEFORE.test(before)) return true;
  // Only this cue's own tail; a parenthetical, comma or colon starts another.
  const tail = phrase.slice(index + length).split(/[(,:]/)[0] ?? '';
  return NEGATED_TAIL.test(tail);
}

function cues(
  phrase: string,
  pattern: RegExp,
  options: { purposeSensitive?: boolean; skip?: (before: string) => boolean } = {},
): RegExpMatchArray[] {
  return [...phrase.matchAll(pattern)].filter(match => {
    const before = phrase.slice(0, match.index);
    if (options.skip?.(before)) return false;
    return !cueIsNegated(phrase, match.index!, match[0].length, options.purposeSensitive ?? false);
  });
}

function statisticRole(word: string): 'facility-demand-average' | 'facility-demand-peak' {
  return /^(?:average|avg|mean)$/i.test(word) ? 'facility-demand-average' : 'facility-demand-peak';
}

/** Role of one label phrase, or null when it names none. */
function roleOfLabelPhrase(phrase: string): WaterQuantityRole | null {
  const systemActor = cues(phrase, SYSTEM_ACTOR_CUE).length > 0;
  const systemOperation = cues(phrase, SYSTEM_OPERATION_CUE).length > 0;
  const subjectSite = SUBJECT_SITE_CUE.test(phrase.replace(SYSTEM_ACTOR_CUE, ' '));
  const systemOnly = (systemActor || systemOperation) && !subjectSite;

  if (cues(phrase, WITHDRAWAL_CUE).length) return 'withdrawal-makeup';
  if (cues(phrase, MARGIN_STRONG_CUE).length) return 'allocation-margin';
  if (cues(phrase, MARGIN_WEAK_CUE).length && !systemOnly) return 'allocation-margin';

  const demand = cues(phrase, DEMAND_CUE).at(-1);
  if (demand) return systemOnly ? 'utility-system-operating' : statisticRole(demand[1]!);
  const wholeSite = cues(phrase, WHOLE_SITE_SCOPE_CUE).length > 0;
  const consumption = cues(phrase, CONSUMPTION_CUE, { purposeSensitive: true }).length > 0;
  // "Peak whole-site consumption" is a whole-site demand, not a cooling
  // quantity. A whole-site allocation with a statistic is still an allocation.
  if (wholeSite && (consumption || cues(phrase, USE_NOUN_CUE).length)) {
    const statistic = cues(phrase, STATISTIC_CUE).at(-1);
    if (statistic) return statisticRole(statistic[1]!);
  }

  const limit = cues(phrase, LIMIT_CUE, {
    skip: before => OPERATING_CONDITION_BEFORE.test(before),
  }).length > 0;
  if (cues(phrase, CONSUMPTIVE_LIMIT_NOUN_PHRASE, { purposeSensitive: true }).length
    || (consumption && limit)) return 'consumptive-use-limit';
  if (consumption && !wholeSite) return 'cooling-consumption';

  if (cues(phrase, ALLOCATION_STRONG_CUE).length) return 'site-allocation';
  if (cues(phrase, ALLOCATION_WEAK_CUE).length && !systemOnly) return 'site-allocation';
  if (cues(phrase, FIRM_CUE).length && cues(phrase, DELIVERY_CUE).length) return 'firm-utility-delivery';
  if (systemActor || systemOperation) return 'utility-system-operating';
  return null;
}

// ---------------------------------------------------------------------------
// Carry rules
// ---------------------------------------------------------------------------

const EQUIVALENCE_WORDS = new Set(['=', '≈', '~', '(', '/', 'or', 'i.e.', 'i.e', 'equivalent', 'equal', 'equals',
  'to', 'which', 'is', 'that', 'approximately', 'approx.', 'about', 'roughly']);
const EQUIVALENCE_MARKERS = new Set(['=', '≈', '~', '(', '/', 'or', 'i.e.', 'i.e', 'equivalent', 'equal', 'equals']);

/** "0.075 MGD, equivalent to 75,000 gal/day", "75,000 gpd (0.075 MGD)", "X = Y". */
function isEquivalenceGap(gap: string): boolean {
  const words = gap.toLowerCase().replace(/([=≈~(/])/g, ' $1 ').split(/[\s,]+/).filter(Boolean);
  return words.length > 0 && words.every(word => EQUIVALENCE_WORDS.has(word))
    && words.some(word => EQUIVALENCE_MARKERS.has(word));
}

/** "... of 1.5 MGD with a max of 4.6 MGD", "... 25,000 gpd and a peak of 55,000 gpd". */
const STATISTIC_CONTINUATION = /^\s*,?\s*(?:(?:and|with)\s+)?(?:(?:an?|the)\s+)?(average|avg|mean|peak|maximum|max|design[- ]day)(?:[- ]day|\s+daily|\s+day)?\s*(?:of|at|is|was|:)?\s*$/i;

function continueRole(base: WaterQuantityRole | null, statistic: string): WaterQuantityRole | null {
  switch (base) {
    case 'facility-demand-average':
    case 'facility-demand-peak':
      return statisticRole(statistic);
    case 'utility-system-operating':
    case 'cooling-consumption':
    case 'withdrawal-makeup':
      return base;
    default:
      // An allocation, limit, margin or firm delivery has no average/peak.
      return null;
  }
}

/** "55,000 gpd consumption (basis) unknown": the following noun is disclaimed. */
const FOLLOWING_RUN_NEGATED = /^\s*(?:(?:is|are|was|were|remains?|basis|still|currently|yet)\s+){0,3}(?:unknown|unspecified|unconfirmed|unquantified|undetermined|not\s+(?:yet\s+)?(?:known|established|determined|specified|quantified|confirmed|modell?ed))\b/i;

/**
 * A closed role noun phrase immediately after the number and its unit:
 * "75,000 gpd allocation", "55,000 gpd of consumptive use", "0.055 MGD peak
 * project water demand". Only whitespace, one parenthetical (an equivalent
 * unit) and "of" may intervene. "for ..." (purpose), a verb or another clause
 * ends the run.
 */
function followingRoleRun(text: string, numberEnd: number): { phrase: string; end: number } | undefined {
  let cursor = numberEnd;
  const unit = LEADING_WATER_UNIT.exec(text.slice(cursor));
  if (unit) cursor += unit[0].length;
  const lead = /^\s*(?:\([^()]{0,60}\)\s*)?(?:of\s+)?/i.exec(text.slice(cursor));
  if (lead) cursor += lead[0].length;
  const words: string[] = [];
  let end = cursor;
  while (words.length < MAX_FOLLOWING_ROLE_WORDS) {
    const word = /^(\s*)([A-Za-z][\w/-]*)/.exec(text.slice(end));
    if (!word || !FOLLOWING_ROLE_WORD.test(word[2]!)) break;
    words.push(word[2]!);
    end += word[0].length;
  }
  if (!words.length || FOLLOWING_RUN_NEGATED.test(text.slice(end, end + 80))) return undefined;
  return { phrase: words.join(' '), end };
}

function classifyAt(
  text: string, start: number, end: number, depth: number, useFollowing: boolean,
): WaterQuantityRole | null {
  if (depth > MAX_CARRY_DEPTH) return null;
  const clauseStart = clauseStartBefore(text, start);
  const previous = lastWaterQuantity(text, clauseStart, start);
  let phraseStart = clauseStart;
  if (previous) {
    const gap = text.slice(previous.end, start);
    if (isEquivalenceGap(gap)) {
      const carried = classifyAt(text, previous.start, previous.numberEnd, depth + 1, useFollowing);
      if (carried || !useFollowing) return carried;
      const run = followingRoleRun(text, end);
      return run ? roleOfLabelPhrase(run.phrase) : null;
    }
    const continuation = STATISTIC_CONTINUATION.exec(gap);
    if (continuation) {
      return continueRole(classifyAt(text, previous.start, previous.numberEnd, depth + 1, true), continuation[1]!);
    }
    phraseStart = previous.end;
    // A role noun that followed the earlier number belongs to it when it had
    // no label of its own ("The 75,000 gal/day consumptive-use capacity
    // leaves a 20,000 gal/day margin"); it cannot also label this number.
    const previousRun = followingRoleRun(text, previous.numberEnd);
    if (previousRun && previousRun.end <= start
      && classifyAt(text, previous.start, previous.numberEnd, depth + 1, false) === null) {
      phraseStart = Math.max(phraseStart, previousRun.end);
    }
  }
  const phrase = text.slice(phraseStart, start);
  const own = roleOfLabelPhrase(phrase);
  if (own || !useFollowing) return own;
  const run = followingRoleRun(text, end);
  return run ? roleOfLabelPhrase(`${phrase} ${run.phrase}`) : null;
}

/**
 * Role of the number printed at text.slice(tokenStart, tokenEnd), or null
 * (UNKNOWN). Pure; never converts units; never reads another clause.
 */
export function classifyWaterQuantityRole(text: string, tokenStart: number, tokenEnd: number): WaterQuantityRole | null {
  if (typeof text !== 'string' || !Number.isInteger(tokenStart) || !Number.isInteger(tokenEnd)
    || tokenStart < 0 || tokenEnd <= tokenStart || tokenEnd > text.length) return null;
  const clause = text.slice(clauseStartBefore(text, tokenStart), clauseEndAfter(text, tokenEnd));
  if (/\brespectively\b/i.test(clause)) return null;
  return classifyAt(text, tokenStart, tokenEnd, 0, true);
}

// ---------------------------------------------------------------------------
// SED whole-site ledger matcher
// ---------------------------------------------------------------------------

/** US gallons per million gallons; the MGD <-> gal/day factor. */
export const GALLONS_PER_MGD = 1_000_000;
/** Half a gallon per day absorbs MGD decimal rounding, never a different figure. */
export const DEFAULT_WHOLE_SITE_WATER_TOLERANCE_GAL_PER_DAY = 0.5;

export const WHOLE_SITE_WATER_LEDGER_KEYS = [
  'siteWaterAllocationMgd',
  'projectAverageWaterDemandMgd',
  'projectPeakWaterDemandMgd',
  'projectWaterMarginMgd',
] as const satisfies readonly SiteResourceKey[];
export type WholeSiteWaterLedgerKey = typeof WHOLE_SITE_WATER_LEDGER_KEYS[number];

export const WHOLE_SITE_WATER_LEDGER_ROLES: Readonly<Record<WholeSiteWaterLedgerKey, WaterQuantityRole>> = {
  siteWaterAllocationMgd: 'site-allocation',
  projectAverageWaterDemandMgd: 'facility-demand-average',
  projectPeakWaterDemandMgd: 'facility-demand-peak',
  projectWaterMarginMgd: 'allocation-margin',
};

export interface WholeSiteWaterLedgerEntry {
  readonly key: WholeSiteWaterLedgerKey;
  readonly role: WaterQuantityRole;
  /** The source-stated value as recorded in the ledger (MGD). */
  readonly valueMgd: number;
  /** valueMgd × GALLONS_PER_MGD. */
  readonly galPerDay: number;
  readonly assumed: boolean;
  readonly fictional: boolean;
}

/**
 * Current, accepted whole-site rows of the SED source ledger. Reuses
 * siteResourceViewFromSed / siteResourceSourceRowsFromSed unchanged, so a
 * hash mismatch, a broken manifest binding or conflicting rows give nothing.
 */
export function wholeSiteWaterLedger(sed: SiteResourceSedInput | null | undefined): WholeSiteWaterLedgerEntry[] {
  const view = siteResourceViewFromSed(sed);
  if (!sed || !view) return [];
  const rows = siteResourceSourceRowsFromSed(sed);
  const reviews = sed.waterResource?.provenanceDetail?.fieldReviews;
  return WHOLE_SITE_WATER_LEDGER_KEYS.flatMap(key => {
    const fact = view.facts.find(row => row.key === key);
    if (!fact || typeof fact.value !== 'number' || !Number.isFinite(fact.value) || fact.value < 0) return [];
    const review = reviews?.[key];
    if (review && review.decision !== 'accepted') return [];
    if (!rows.some(row => row.key === key && row.value === fact.value)) return [];
    return [{
      key, role: WHOLE_SITE_WATER_LEDGER_ROLES[key], valueMgd: fact.value,
      galPerDay: fact.value * GALLONS_PER_MGD, assumed: fact.assumed, fictional: fact.fictional,
    }];
  });
}

/** Ledger keys whose daily value equals `galPerDay` within the tolerance. */
export function matchWholeSiteWaterRoles(
  ledger: readonly WholeSiteWaterLedgerEntry[],
  galPerDay: number,
  tolGalPerDay: number = DEFAULT_WHOLE_SITE_WATER_TOLERANCE_GAL_PER_DAY,
): WholeSiteWaterLedgerKey[] {
  if (!Number.isFinite(tolGalPerDay) || tolGalPerDay < 0) {
    throw new RangeError('Whole-site water tolerance must be a finite, non-negative gal/day value.');
  }
  if (!Number.isFinite(galPerDay) || galPerDay < 0) return [];
  return WHOLE_SITE_WATER_LEDGER_KEYS.filter(key => ledger.some(entry =>
    entry.key === key && Math.abs(entry.galPerDay - galPerDay) <= tolGalPerDay));
}

/**
 * Which whole-site ledger roles (allocation, average/peak demand, margin) the
 * SED states at exactly this daily rate. [] when there is no current,
 * accepted ledger value at that rate.
 */
export function wholeSiteWaterRolesAt(
  sed: SiteResourceSedInput | null | undefined,
  galPerDay: number,
  tolGalPerDay: number = DEFAULT_WHOLE_SITE_WATER_TOLERANCE_GAL_PER_DAY,
): WholeSiteWaterLedgerKey[] {
  return matchWholeSiteWaterRoles(wholeSiteWaterLedger(sed), galPerDay, tolGalPerDay);
}
